import React from 'react';
import { Modal } from 'antd';
import { LightningIcon, XIcon } from '@phosphor-icons/react';
import styled from 'styled-components';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { radius } from '../../theme/radius';
import upgradeModalBg from './assets/upgrade-modal-bg.png';
import upgradeIllustration from './assets/upgrade-illustration.png';

export type UpgradePromptModalProps = {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  title: string;
  description: React.ReactNode;
  upgradeLabel: string;
  dismissLabel: string;
};

export const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  open,
  onClose,
  onUpgrade,
  title,
  description,
  upgradeLabel,
  dismissLabel,
}) => (
  <StyledModal
    open={open}
    footer={null}
    closable={false}
    maskClosable
    onCancel={onClose}
    width={776}
    centered
  >
    <StyledWrapper>
      <StyledBackground />
      <StyledContent>
        <StyledCloseButton onClick={onClose} aria-label="Close">
          <XIcon size={15} weight="bold" />
        </StyledCloseButton>
        <StyledIllustration src={upgradeIllustration as unknown as string} alt="" aria-hidden="true" />
        <StyledTitle>{title}</StyledTitle>
        <StyledDescription>{description}</StyledDescription>
        <StyledButtonGroup>
          <StyledUpgradeButton onClick={onUpgrade}>
            <LightningIcon size={16} weight="fill" />
            {upgradeLabel}
          </StyledUpgradeButton>
          <StyledDismissButton onClick={onClose}>
            {dismissLabel}
          </StyledDismissButton>
        </StyledButtonGroup>
      </StyledContent>
    </StyledWrapper>
  </StyledModal>
);

/* ---------- Styled Components ---------- */

const StyledModal = styled(Modal)`
  max-width: 92vw;

  .ant-modal-content {
    padding: 0;
    border-radius: ${radius.box}px;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const StyledWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledBackground = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${upgradeModalBg as unknown as string});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  pointer-events: none;
`;

const StyledContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 72px 80px 62px;

  @media (width <= 640px) {
    padding: 48px 24px 40px;
  }
`;

const StyledCloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 1;
  color: ${colors.secondary[700]};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.6;
  }
`;

const StyledTitle = styled.h2`
  margin: 12px 0 0;
  font-family: ${typography.fontFamily.primary};
  font-size: 48px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.secondary[700]};
  text-align: center;
  line-height: 57.6px;
  letter-spacing: -1.44px;
  max-width: 530px;
  position: relative;

  @media (width <= 640px) {
    font-size: 32px;
    line-height: 1.2;
    letter-spacing: -0.96px;
  }
`;

const StyledDescription = styled.div`
  margin: 24px 0 0;
  font-family: ${typography.fontFamily.secondary};
  font-size: ${typography.body.medium.fontSize}px;
  font-weight: ${typography.fontWeight.regular};
  color: ${colors.secondary[700]};
  text-align: center;
  line-height: 25.6px;
  max-width: 530px;
  white-space: pre-line;
  position: relative;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 48px;
  gap: 4px;
  position: relative;
`;

const StyledUpgradeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(92deg, #6946d5 22.22%, #5423ec 80.77%);
  color: ${colors.neutral.white};
  border: 2px solid ${colors.primary[500]};
  border-radius: ${radius.pill}px;
  padding: 12px 24px 12px 18px;
  font-family: ${typography.fontFamily.secondary};
  font-size: ${typography.body.small.fontSize}px;
  font-weight: ${typography.fontWeight.medium};
  letter-spacing: -0.14px;
  cursor: pointer;
  box-shadow: 0 3px 30px rgba(105, 70, 213, 0.3);
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const StyledDismissButton = styled.button`
  background: none;
  border: none;
  border-radius: ${radius.pill}px;
  padding: 12px 24px;
  cursor: pointer;
  font-family: ${typography.fontFamily.secondary};
  font-size: ${typography.body.medium.fontSize}px;
  font-weight: ${typography.fontWeight.regular};
  line-height: 25.6px;
  color: ${colors.secondary[700]};
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const StyledIllustration = styled.img`
  position: relative;
  width: 238px;
  height: 239px;
  flex-shrink: 0;
  user-select: none;
  pointer-events: none;
`;
